import React, { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Megaphone, Camera, Play } from 'lucide-react';
import PhotoSlideshow from './PhotoSlideshow';
import { useAuth } from '../context/AuthContext';
import PhotoFrame from './PhotoFrame';

// Public photo strips under an event (pre-event advertising + photos from the event) with a lightbox.
export default function EventPhotoStrip({ photos, eventId, eventTitle = '' }) {
  const { mediaUrl } = useAuth();
  const [show, setShow] = useState(null); // group
  const [lb, setLb] = useState(null); // {list, idx}
  useEffect(() => {
    if (!lb) return undefined;
    const k = (e) => { if (e.key === 'Escape') setLb(null); if (e.key === 'ArrowLeft') setLb((s) => ({ ...s, idx: (s.idx - 1 + s.list.length) % s.list.length })); if (e.key === 'ArrowRight') setLb((s) => ({ ...s, idx: (s.idx + 1) % s.list.length })); };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, [lb]);
  const groups = [
    { key: 'promo', label: 'Pre-event photos', icon: Megaphone, list: (photos && photos.promo) || [] },
    { key: 'event', label: 'Photos from the event', icon: Camera, list: (photos && photos.event) || [] },
  ].filter((g) => g.list.length);
  if (!groups.length) return null;
  const cur = lb ? lb.list[lb.idx] : null;
  return (
    <div className="mt-3 space-y-3" data-testid={`event-photo-strip-${eventId}`}>
      {groups.map((g) => (
        <div key={g.key} data-testid={`event-strip-${g.key}-${eventId}`}>
          <div className="text-xs font-title flex items-center gap-2 mb-1" style={{ color: 'var(--olive)' }}><g.icon size={12} /> {g.label}
            <button onClick={() => setShow(g)} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px]" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid={`event-slideshow-${g.key}-${eventId}`}><Play size={10} /> Slideshow</button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {g.list.map((p, i) => (
              <button key={p.id} onClick={() => setLb({ list: g.list, idx: i })} className="flex-shrink-0 rounded overflow-hidden border" style={{ borderColor: 'rgba(0,0,0,0.12)', width: 128 }} title={p.title} data-testid={`event-strip-photo-${p.id}`}>
                <PhotoFrame src={mediaUrl(p.url)} display={p.display} alt={p.title} className="w-32 h-24" style={{ background: 'rgba(0,0,0,0.04)' }} />
                {p.title && <span className="block text-[10px] truncate px-1 py-0.5 text-left" style={{ color: 'var(--navy)' }}>{p.title}</span>}
              </button>
            ))}
          </div>
        </div>
      ))}
      {show && <PhotoSlideshow photos={show.list} title={`${eventTitle ? `${eventTitle} — ` : ''}${show.label}`} mediaUrl={mediaUrl} onClose={() => setShow(null)} />}
      {cur && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.9)' }} onClick={() => setLb(null)} data-testid="event-lightbox">
          <button className="absolute top-5 right-5 text-white" onClick={() => setLb(null)} data-testid="event-lightbox-close"><X size={30} /></button>
          {lb.list.length > 1 && <button className="absolute left-4 text-white" onClick={(e) => { e.stopPropagation(); setLb((s) => ({ ...s, idx: (s.idx - 1 + s.list.length) % s.list.length })); }}><ChevronLeft size={40} /></button>}
          {lb.list.length > 1 && <button className="absolute right-4 text-white" onClick={(e) => { e.stopPropagation(); setLb((s) => ({ ...s, idx: (s.idx + 1) % s.list.length })); }}><ChevronRight size={40} /></button>}
          <div onClick={(e) => e.stopPropagation()} className="max-w-5xl w-full">
            <PhotoFrame src={mediaUrl(cur.url)} display={cur.display} alt={cur.title} className="w-full h-[70vh] rounded" />
            <div className="mt-3 text-center" style={{ color: 'var(--cream)' }}>
              {cur.title && <h3 className="font-title text-lg">{cur.title}</h3>}
              {cur.description && <p className="text-sm opacity-90 mt-1">{cur.description}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
